import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import classNames from 'classnames';
import './index.scss';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import { Collapsible } from '@douyinfe/semi-ui';
import UserMenu from '@/components/Navigation/UserMenu';
import { settingBillingThunk } from '@/store/reducers/settingBillingReducer';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { BILLING_TABS, getBillingPath } from '../../config/billing';
import Icon from '../Icon';
import StatusButton from './StatusButton';
import FooterLink from './FooterLink';

/**
 * interfaces & types
 */
export declare type NavigationItemProps = {
  key: string;
  icon: string;
  link?: string;
  target?: string;
  replace?: boolean;
  titleSubfixIcon?: string;
  hoverSubfix?: string;
  content?: NavigationItemProps[];
}

type NavigationGroupProps = {
  title: string,
  titleIcon?: React.ReactElement,
  items: NavigationItemProps[]
  cls?: string
}

/**
 * constants
 */
const defaultNavigationGroupProps: NavigationGroupProps = {
  title: '',
  titleIcon: undefined,
  items: [],
  cls: ''
};

function formatIntegerString(amount: string): string {
  const isNegative = amount.startsWith('-');
  const normalizedAmount = amount.replace('-', '');
  const formattedAmount = normalizedAmount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return isNegative ? `-${formattedAmount}` : formattedAmount;
}

function parseCreditAmount(amount?: string): number {
  return Number(amount) || 0;
}

function getRemainingCredits(plan?: {
  credit_total?: string;
  credit_used?: string;
}, extra?: {
  credit_total?: string;
  credit_used?: string;
} | null): string {
  if (plan?.credit_total === '-1') {
    return 'Unlimited';
  }

  const remainingCredits = parseCreditAmount(plan?.credit_total)
    + parseCreditAmount(extra?.credit_total)
    - parseCreditAmount(plan?.credit_used)
    - parseCreditAmount(extra?.credit_used);

  return formatIntegerString(`${Math.max(remainingCredits, 0)}`);
}

function isUnlimitedPlan(creditTotal?: string): boolean {
  return creditTotal === '-1';
}

/**
 * function component
 */
function NavIcon({ className, icon }: Omit<{
  className?: string,
  icon: string | null
}, ''>): React.ReactElement | null {
  if (!icon) {
    return null;
  }
  return (
    <Icon className={classNames('icon', className)} icon={icon} />
  );
}

export function NavigationItem(props: {
  item: NavigationItemProps;
  isCollapseTitle?: boolean;
  onClick?: () => void;
  isSubdirectory?: boolean;
}): React.ReactElement {
  const { t } = useTranslation();
  const {
    item,
    isCollapseTitle,
    isSubdirectory,
    onClick
  } = props;

  const content = (
    <>
      <NavIcon icon={item.icon} className={_.last(item.icon.split('/'))} />
      <span>{t(item.key)}</span>
      <span>
        {item.titleSubfixIcon && (
        <Icon
          className="navigation-item-title-subfix"
          icon={item.titleSubfixIcon}
        />
        )}
      </span>
      {item.hoverSubfix && (
      <Icon
        className="icon hover-subfix"
        icon={item.hoverSubfix}
        style={{ position: 'absolute', right: 0 }}
      />
      )}
    </>
  );

  if (isCollapseTitle) {
    if (!onClick) {
      throw new Error('Unexpected undefined of \'onClick\' when item is collapse title');
    }

    return (
      <button
        type="button"
        className={classNames(['navigation-item'])}
        onClick={() => {
          onClick();
          const arrowIcon = document.querySelector('.collapse-arrow');
          arrowIcon?.classList.toggle('expanded');
        }}
      >
        {content}
        <Icon icon="common/arrow-left" className="collapse-arrow" />
      </button>
    );
  }

  if (!item.link) {
    return (
      <div className={
        classNames(
          ['navigation-item', 'medium', 'disabled', 'relative']
        )
        }
      >
        {content}
        <Icon className="soon" icon="navigation/soon" />
      </div>
    );
  }

  if (item.link.startsWith('http')) {
    return (
      <a
        className={classNames('navigation-item', 'regular', 'relative')}
        href={item.link}
        target={item.target}
        rel="noreferrer"
      >
        {content}
      </a>
    );
  }

  return (
    <NavLink
      key={item.key}
      className={({
        isActive
      }) => classNames(
        ['navigation-item', {
          'active medium': isActive,
          regular: !isActive
        }, {
          'navigation-item-subdirectory': isSubdirectory
        }]
      )}
      to={item.link}
      target={item.target}
      replace={item.replace}
    >
      {content}
    </NavLink>
  );
}

function NavigationCollapse({ item }:{item: NavigationItemProps}): React.ReactElement {
  const {
    key, icon, content, titleSubfixIcon, hoverSubfix
  } = item;

  if (!content) {
    throw new Error('Unexpected \'undefined\' of content');
  }

  const [isOpen, setIsOpen] = useState(false);

  return (
    <div>
      <NavigationItem
        item={{
          key,
          icon,
          titleSubfixIcon,
          hoverSubfix
        }}
        isCollapseTitle
        onClick={() => setIsOpen((prev) => !prev)}
      />
      <Collapsible isOpen={isOpen}>
        {content.map((navItem) => (
          <NavigationItem key={navItem.key} item={navItem} isSubdirectory />
        ))}
      </Collapsible>
    </div>
  );
}

function NavigationGroup({
  title,
  titleIcon,
  items,
  cls
} = defaultNavigationGroupProps): React.ReactElement {
  return (
    <div className={`navigation-group ${cls}`}>
      <h3 className={classNames('title', 'bolder')}>
        {title}
        {titleIcon}
      </h3>
      {
        _.map(items, (item) => (item.content
          ? <NavigationCollapse key={item.key} item={item} />
          : <NavigationItem key={item.key} item={item} />))
      }
    </div>
  );
}

export default function Navigation(): React.ReactElement {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isCreditLoading, setIsCreditLoading] = useState(true);
  const creditStatus = useAppSelector((state) => state.settingBilling.creditStatus);
  const remainingCredits = getRemainingCredits(creditStatus?.plan, creditStatus?.extra);
  const isUnlimitedCreditsPlan = isUnlimitedPlan(creditStatus?.plan.credit_total);

  useEffect(() => {
    setIsCreditLoading(true);
    dispatch(settingBillingThunk.getCreditStatus()).finally(() => {
      setIsCreditLoading(false);
    });
  }, [dispatch]);

  const APINavigationItems: NavigationItemProps[] = [
    {
      key: 'navigation.dashboard',
      icon: 'navigation/dashboard',
      link: '/'
    },
    {
      key: 'navigation.logExplorer',
      icon: 'navigation/logExplorer',
      link: '/logExplorer'
    }
  ];

  const DataCloudItems: NavigationItemProps[] = [
    {
      key: 'navigation.query',
      icon: 'navigation/dataCloud',
      link: '/dataCloud/v2'
    },
    {
      key: 'navigation.archived',
      icon: 'navigation/archivedQuery',
      link: '/archivedQueries'
    },
    {
      key: 'navigation.apis',
      icon: 'navigation/apis',
      link: '/apis'
    }
  ];

  const SuiteNavigationItems: NavigationItemProps[] = [
    {
      key: 'navigation.sync',
      icon: 'navigation/pipelines',
      titleSubfixIcon: 'navigation/sync/new',
      content: [{
        key: 'navigation.sync-jobs',
        icon: 'navigation/sync/jobs',
        link: '/sync/jobs'
      }, {
        key: 'navigation.sync-integrations',
        icon: 'navigation/sync/integrations',
        link: '/sync/integrations'
      }, {
        key: 'navigation.sync-webhook',
        icon: 'navigation/sync/webhook',
        link: '/sync/webhook'
      }, {
        key: 'navigation.sync-kafka',
        icon: 'navigation/sync/kafka',
        link: '/sync/kafka'
      }]
    },
    // {
    //   key: 'navigation.pipelines',
    //   icon: 'navigation/pipelines',
    //   link: '/pipelines'
    // },
    {
      key: 'navigation.datasets',
      icon: 'navigation/datasets',
      link: 'https://platform.chainbase.com/datasets',
      hoverSubfix: 'common/open',
      target: '__blank'
    }
    // {
    //   key: 'navigation.usage',
    //   icon: 'navigation/usage',
    //   link: '/usage'
    // }
  ];

  const footerLinkItems: NavigationItemProps[] = [
    {
      key: 'navigation.documents',
      icon: 'navigation/document',
      link: 'https://docs.chainbase.com/platform/overview'
    },
    {
      key: 'navigation.help',
      icon: 'navigation/help',
      link: 'https://platform.chainbase.com/help'
    }
  ];

  return (
    <div className="navigation">
      <a className="logo-link" href="/">
        <Icon className="logo" icon="header/Logo" />
      </a>
      <div className="navigation-list">
        <NavigationGroup
          title={t('navigation.apiIntelligence')}
          items={APINavigationItems}
          cls="navigation-group-api"
        />

        <NavigationGroup
          title={t('navigation.dataCloudTitle')}
          items={DataCloudItems}
          cls="navigation-group-datacloud"
        />

        <NavigationGroup
          title={t('navigation.dataInfrastructure')}
          items={SuiteNavigationItems}
          cls="navigation-group-dataInfura"
        />
      </div>

      <div className="bottom-group">

        <div className="p-4 border border-[#444D56] border-solid border-x-0">
          <button
            type="button"
            className="navigation-credits"
            onClick={() => navigate(getBillingPath(
              isUnlimitedCreditsPlan ? BILLING_TABS.planBilling : BILLING_TABS.credit
            ))}
          >
            <span className="navigation-credits__label">Credits</span>
            <div className="navigation-credits__value">
              {isCreditLoading ? (
                <svg
                  className="navigation-credits__badge"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M8.00109 13.0377C10.7836 13.0377 13.0392 10.7821 13.0392 7.99962C13.0392 5.21716 10.7836 2.96153 8.00109 2.96153C5.21862 2.96153 2.96299 5.21716 2.96299 7.99962C2.96299 10.7821 5.21862 13.0377 8.00109 13.0377ZM8.00109 15.053C11.8965 15.053 15.0544 11.8951 15.0544 7.99962C15.0544 4.10417 11.8965 0.946289 8.00109 0.946289C4.10564 0.946289 0.947754 4.10417 0.947754 7.99962C0.947754 11.8951 4.10564 15.053 8.00109 15.053Z"
                    fill="#444D56"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M15.0546 7.99987C15.0546 7.99979 15.0546 7.9997 15.0546 7.99962C15.0546 4.10417 11.8967 0.946289 8.00125 0.946289C8.00083 0.946289 8.00042 0.946289 8 0.946289V2.96153C8.00042 2.96153 8.00083 2.96153 8.00125 2.96153C10.7837 2.96153 13.0393 5.21716 13.0393 7.99962C13.0393 7.9997 13.0393 7.99979 13.0393 7.99987H15.0546Z"
                    fill="#6AB6F3"
                  />
                </svg>
              ) : (
                <span>{remainingCredits}</span>
              )}
            </div>
          </button>
          <StatusButton />
          <div className="flex justify-center items-center mt-4">
            <FooterLink data={footerLinkItems[0]} />
            <div className="border-[0.5px] border-solid border-[#444D56]" />
            <FooterLink data={footerLinkItems[1]} />
          </div>
        </div>
        <UserMenu />
      </div>
    </div>
  );
}
